/**
 * Subscription Service
 * Handles PayPal subscription management and database operations
 */

import { supabase } from '../lib/supabase'

// Subscription plans configuration
export const SUBSCRIPTION_PLANS = {
  start: {
    id: 'start',
    name: 'Start Plan',
    price: 9.00,
    priceDisplay: '$9',
    annualPrice: 7.00,
    annualPriceDisplay: '$7',
    annualTotal: 84,
    interval: 'month',
    features: [
      '5 active projects',
      '10GB storage',
      'Basic version control',
      'Email support',
      'Client review links',
      '30-day history'
    ],
    paypalPlanId: import.meta.env.VITE_PAYPAL_START_PLAN_ID,
    paypalAnnualPlanId: import.meta.env.VITE_PAYPAL_START_ANNUAL_PLAN_ID
  },
  pro: {
    id: 'pro',
    name: 'Pro Plan',
    price: 15.00,
    priceDisplay: '$15',
    annualPrice: 12.00,
    annualPriceDisplay: '$12',
    annualTotal: 144,
    interval: 'month',
    features: [
      'Unlimited projects',
      '100GB storage',
      'Advanced version control',
      'Priority support',
      'Custom branding',
      'Team collaboration',
      'Unlimited history',
      'Advanced analytics'
    ],
    paypalPlanId: import.meta.env.VITE_PAYPAL_PRO_PLAN_ID,
    paypalAnnualPlanId: import.meta.env.VITE_PAYPAL_PRO_ANNUAL_PLAN_ID
  }
}

/**
 * Get current organization ID for logged-in user
 */
export async function getCurrentOrganization() {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('organization_members')
      .select('organization_id, organization:organizations(*)')
      .eq('user_id', user.id)
      .limit(1)

    if (error) throw error
    if (!data || data.length === 0) throw new Error('No organization found')
    
    return data[0]
  } catch (error) {
    console.error('Error fetching organization:', error)
    throw error
  }
}

/**
 * Get active subscription for user's organization
 */
export async function getActiveSubscription() {
  try {
    const orgData = await getCurrentOrganization()

    // Fetch active OR trial subscriptions
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', orgData.organization_id)
      .in('status', ['active', 'trial'])
      .order('created_at', { ascending: false })
      .limit(1)

    if (error) throw error

    const sub = data && data.length > 0 ? data[0] : null

    // App-level expiry check (fallback when cron job hasn't run yet)
    if (sub && isSubscriptionExpiredInternal(sub)) {
      // Mark as expired in DB asynchronously — don't block the user's request
      supabase
        .from('subscriptions')
        .update({ status: 'expired', updated_at: new Date().toISOString() })
        .eq('id', sub.id)
        .then(() => {})
        .catch(() => {})
      return null
    }

    return sub
  } catch (error) {
    console.error('Error fetching subscription:', error)
    return null
  }
}

// Internal helper — avoids circular dependency with exported isSubscriptionExpired
function isSubscriptionExpiredInternal(sub) {
  if (!sub) return true
  const now = new Date()
  if (sub.is_trial) return now > new Date(sub.trial_ends_at)
  return now > new Date(sub.current_period_end)
}

/**
 * Get all subscriptions for organization (history)
 */
export async function getSubscriptionHistory() {
  try {
    const orgData = await getCurrentOrganization()
    
    const { data, error } = await supabase
      .from('subscriptions')
      .select('*')
      .eq('organization_id', orgData.organization_id)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching subscription history:', error)
    return []
  }
}

/**
 * Create a new subscription after successful PayPal payment.
 * If an active subscription already exists it is cancelled first (upgrade/downgrade).
 */
export async function createSubscription(subscriptionData) {
  try {
    const orgData = await getCurrentOrganization()
    const orgId = orgData.organization_id

    // --- Deactivate any existing active/trial subscription first ---
    const { data: existing } = await supabase
      .from('subscriptions')
      .select('id, paypal_subscription_id')
      .eq('organization_id', orgId)
      .in('status', ['active', 'trial'])
      .limit(1)

    if (existing && existing.length > 0) {
      const old = existing[0]
      await supabase
        .from('subscriptions')
        .update({
          status: 'cancelled',
          cancelled_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', old.id)
    }

    const billingCycle = subscriptionData.billing_cycle || 'monthly'

    const { data, error } = await supabase
      .from('subscriptions')
      .insert([{
        organization_id: orgId,
        plan_name: subscriptionData.plan_name,
        plan_price: subscriptionData.plan_price,
        billing_cycle: billingCycle,
        status: subscriptionData.status || 'active',
        current_period_start: new Date().toISOString(),
        current_period_end: calculatePeriodEnd(billingCycle),
        paypal_subscription_id: subscriptionData.paypal_subscription_id,
        paypal_order_id: subscriptionData.paypal_order_id,
        paypal_payer_id: subscriptionData.paypal_payer_id,
        paypal_email: subscriptionData.paypal_email,
        metadata: subscriptionData.metadata || {}
      }])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error creating subscription:', error)
    throw error
  }
}

/**
 * Update subscription status (e.g., after webhook)
 */
export async function updateSubscriptionStatus(subscriptionId, status, metadata = {}) {
  try {
    const updates = {
      status,
      updated_at: new Date().toISOString()
    }

    if (status === 'cancelled') {
      updates.cancelled_at = new Date().toISOString()
    }

    if (metadata) {
      updates.metadata = metadata
    }

    const { data, error } = await supabase
      .from('subscriptions')
      .update(updates)
      .eq('id', subscriptionId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error updating subscription:', error)
    throw error
  }
}

/**
 * Cancel subscription — updates local DB AND cancels on PayPal.
 * The backend Netlify Function handles the actual PayPal API call so the
 * secret credentials are never exposed to the browser.
 */
export async function cancelSubscription(subscriptionId) {
  try {
    // 1. Fetch the subscription to get the PayPal subscription ID
    const { data: sub, error: fetchError } = await supabase
      .from('subscriptions')
      .select('id, paypal_subscription_id')
      .eq('id', subscriptionId)
      .single()

    if (fetchError) throw fetchError

    // 2. Cancel on PayPal side (server-side via Netlify function)
    if (sub.paypal_subscription_id) {
      try {
        const appUrl = import.meta.env.VITE_APP_URL || ''
        const res = await fetch(`${appUrl}/.netlify/functions/paypal-cancel-subscription`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            subscriptionId: sub.paypal_subscription_id,
            reason: 'Cancelled by user'
          })
        })
        if (!res.ok) {
          console.error('PayPal cancel API returned:', res.status)
        }
      } catch (paypalErr) {
        // Log but don't block — still mark as cancelled in DB
        console.error('PayPal cancel call failed:', paypalErr)
      }
    }

    // 3. Update local DB
    const { data, error } = await supabase
      .from('subscriptions')
      .update({
        status: 'cancelled',
        cancelled_at: new Date().toISOString(),
        cancel_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', subscriptionId)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error cancelling subscription:', error)
    throw error
  }
}

/**
 * Calculate next billing period end based on billing cycle
 */
function calculatePeriodEnd(billingCycle = 'monthly') {
  const now = new Date()
  const periodEnd = new Date(now)
  if (billingCycle === 'annual') {
    periodEnd.setFullYear(periodEnd.getFullYear() + 1)
  } else {
    periodEnd.setDate(periodEnd.getDate() + 30)
  }
  return periodEnd.toISOString()
}

/**
 * Get days remaining in current subscription
 */
export function getDaysRemaining(subscription) {
  if (!subscription || !subscription.current_period_end) return 0
  
  const now = new Date()
  const endDate = new Date(subscription.current_period_end)
  const diffTime = endDate - now
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  return diffDays > 0 ? diffDays : 0
}

/**
 * Check if subscription is expired
 */
export function isSubscriptionExpired(subscription) {
  if (!subscription) return true
  if (!['active', 'trial'].includes(subscription.status)) return true

  const now = new Date()
  if (subscription.is_trial) return now > new Date(subscription.trial_ends_at)
  return now > new Date(subscription.current_period_end)
}

/**
 * Returns trial days remaining (0 if not trial or expired)
 */
export function getTrialDaysRemaining(subscription) {
  if (!subscription?.is_trial || subscription.status !== 'trial') return 0
  const diff = new Date(subscription.trial_ends_at) - new Date()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

/**
 * Returns true when subscription is an active free trial
 */
export function isActiveTrial(subscription) {
  if (!subscription) return false
  return subscription.is_trial === true &&
    subscription.status === 'trial' &&
    new Date() < new Date(subscription.trial_ends_at)
}

/**
 * Get plan details by plan name
 */
export function getPlanDetails(planName) {
  return SUBSCRIPTION_PLANS[planName] || null
}

/**
 * Format price for display
 */
export function formatPrice(price) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(price)
}

/**
 * Verify PayPal payment (server-side - to be called via edge function)
 * This is a placeholder - actual verification happens in backend
 */
export async function verifyPayPalPayment(orderId) {
  try {
    // Call your edge function/API endpoint to verify payment
    const response = await fetch('/api/verify-paypal-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orderId })
    })

    if (!response.ok) throw new Error('Payment verification failed')
    
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error verifying payment:', error)
    throw error
  }
}
